import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import { useAuth } from '../../../contexts/AuthContext';
import { billingAPI } from '../../../lib/api';
import type { CheckoutAttemptStatus } from '../../../lib/billing/checkoutClient';
import { type CheckoutIntent, clearCheckoutIntent } from '../../../lib/billing/checkoutIntent';
import { clearPlanGates } from '../../../lib/plan-gate';

export type CheckoutActivationStatus = 'idle' | CheckoutAttemptStatus;
export type CheckoutPresentationState = 'idle' | 'finalizing' | 'recoverable';
export type CheckoutPresentationSignal = 'returned' | 'dismissed';

const DEFAULT_POLL_INTERVAL_MS = 2000;
const DEFAULT_RECOVERABLE_GRACE_MS = 5000;
const TERMINAL_STATUSES = new Set<CheckoutAttemptStatus>(['approved', 'expired', 'voided']);

export function useCheckoutActivation(
  orgId: string | undefined,
  intent: CheckoutIntent | null,
  options?: {
    pollIntervalMs?: number;
    recoverableGraceMs?: number;
    presentationSignal?: CheckoutPresentationSignal | null;
  },
): {
  status: CheckoutActivationStatus;
  presentationState: CheckoutPresentationState;
  isFinalizing: boolean;
} {
  const queryClient = useQueryClient();
  const { refreshUser } = useAuth();
  const handledStatusRef = useRef<string | null>(null);
  const [graceElapsed, setGraceElapsed] = useState(false);
  const [deadlineReached, setDeadlineReached] = useState(false);
  const attemptId = intent?.attemptId;
  const canPoll =
    !!orgId && !!attemptId && (!intent.organizationId || intent.organizationId === orgId);
  const pollIntervalMs = options?.pollIntervalMs ?? DEFAULT_POLL_INTERVAL_MS;
  const recoverableGraceMs = options?.recoverableGraceMs ?? DEFAULT_RECOVERABLE_GRACE_MS;

  const attemptQuery = useQuery<{ attemptId: string; status: CheckoutAttemptStatus }>({
    queryKey: ['billing-checkout-attempt', orgId, attemptId],
    queryFn: async () => {
      if (!orgId || !attemptId) throw new Error('Checkout attempt context is missing');
      const { data } = await billingAPI.getCheckoutAttempt(orgId, attemptId);
      return data;
    },
    enabled: canPoll,
    retry: 1,
    refetchInterval: (query) => {
      const status = query.state.data?.status;
      return status && TERMINAL_STATUSES.has(status) ? false : pollIntervalMs;
    },
  });

  const status: CheckoutActivationStatus = !canPoll
    ? 'idle'
    : (attemptQuery.data?.status ?? 'pending');

  useEffect(() => {
    setGraceElapsed(false);
    if (!canPoll || !options?.presentationSignal) return;
    const timeout = setTimeout(() => setGraceElapsed(true), recoverableGraceMs);
    return () => clearTimeout(timeout);
  }, [canPoll, options?.presentationSignal, recoverableGraceMs]);

  useEffect(() => {
    setDeadlineReached(false);
    if (!canPoll || !intent?.attemptExpiresAt) return;
    const delay = Math.max(0, Date.parse(intent.attemptExpiresAt) - Date.now());
    const timeout = setTimeout(() => {
      setDeadlineReached(true);
      void attemptQuery.refetch();
    }, delay);
    return () => clearTimeout(timeout);
  }, [attemptQuery.refetch, canPoll, intent?.attemptExpiresAt]);

  const isTerminal = TERMINAL_STATUSES.has(status as CheckoutAttemptStatus);
  const presentationState: CheckoutPresentationState =
    !canPoll || isTerminal
      ? 'idle'
      : attemptQuery.isError ||
          status === 'declined' ||
          status === 'error' ||
          graceElapsed ||
          deadlineReached
        ? 'recoverable'
        : 'finalizing';

  useEffect(() => {
    if (!orgId || !intent || !TERMINAL_STATUSES.has(status as CheckoutAttemptStatus)) return;
    const handledKey = `${intent.idempotencyKey}:${status}`;
    if (handledStatusRef.current === handledKey) return;
    handledStatusRef.current = handledKey;

    void (async () => {
      await clearCheckoutIntent(intent.idempotencyKey);
      if (status !== 'approved') return;
      await refreshUser();
      clearPlanGates();
      await queryClient.invalidateQueries({ queryKey: ['subscription', orgId] });
    })();
  }, [intent, orgId, queryClient, refreshUser, status]);

  return {
    status,
    presentationState,
    isFinalizing: presentationState === 'finalizing',
  };
}
